import { InputButton } from '@abbott-components/core';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { JSONSchema7 } from 'json-schema';
import { useSnackbar } from 'notistack';
import { useContext } from 'react';
import { put, remove } from '../Shared/ajax';
import ControlState from '../Shared/controlState';
import {
  LastUpdatedInfo,
  WorkflowEngineActionKind,
  WorkflowEngineContext
} from '../WorkflowEngine/workflowEngineReducer';
import controlConfig from '../Controls/controlDictionary';
import JSONSchema7Properties from '../Shared/jSONSchema7Properties';
import ControlKind from '../Shared/controlKind';

interface UpdatePageRequest {
  externalId: string;
  metadata: string;
}

interface UpdatePageResponse {
  externalId: string;
  lastUpdatedDate: string;
  lastUpdatedBy: string;
  metadata: string;
}

export default (props: { control: ControlState; onClose: () => void }) => {
  const { control, onClose } = props;
  const { state: workflowEngineState, dispatch } = useContext(
    WorkflowEngineContext
  );
  const { enqueueSnackbar } = useSnackbar();

  const { pageId } = control;

  const saveControlDeletionAsync = async (
    pageId: string,
    pageSchema: JSONSchema7
  ) => {
    const response = await put<UpdatePageResponse, UpdatePageRequest>(
      'DynamicWorkflowPage',
      {
        externalId: pageId,
        metadata: JSON.stringify(pageSchema)
      }
    );

    dispatch({
      type: WorkflowEngineActionKind.SET_LAST_UPDATED_INFO,
      payload: {
        externalId: response.externalId,
        lastUpdatedDate: response.lastUpdatedDate,
        lastUpdatedBy: response.lastUpdatedBy,
        metadata: response.metadata
      } as LastUpdatedInfo
    });
  };

  const deleteAttachmentsAsync = async (
    versionExternalId: string,
    attachmentId: string
  ) => {
    if (!attachmentId) return;

    await remove(
      `DynamicWorkflowVersion/${versionExternalId}/attachment/${attachmentId}`
    );
  };

  const handleConfirmDeleteClick = async () => {
    dispatch({
      type: WorkflowEngineActionKind.CONTROL_DELETE,
      payload: control
    });

    checkForStatus();

    const currentPage = workflowEngineState.pages.find(
      (page) => page.id == pageId
    );

    try {
      await saveControlDeletionAsync(
        pageId as string,
        currentPage?.schema as JSONSchema7
      );

      const attachmentId = (
        control.schema?.properties as JSONSchema7Properties
      )['externalId']?.default as string;

      if (controlConfig[control.kind].hasAttachment && attachmentId) {
        await deleteAttachmentsAsync(
          workflowEngineState.versionExternalId,
          attachmentId
        );
      }

      enqueueSnackbar('Control was successfully deleted!', {
        variant: 'success'
      });
    } catch (err) {
      enqueueSnackbar((err as Error).message, { variant: 'error' });
    }
  };

  const checkForStatus = () => {
    if (control.kind != ControlKind.SELECT) return;

    const controlProps = (control.schema as JSONSchema7).properties;
    if (controlProps && controlProps.statusEnabled) {
      const statusValue = controlProps.statusEnabled as JSONSchema7;
      if (statusValue.default)
        dispatch({
          type: WorkflowEngineActionKind.SET_STATUS_ENABLE,
          payload: false
        });
    }
  };

  return (
    <Stack>
      <Typography>Are you sure you want to delete this control?</Typography>
      <Stack direction="row" mt={2} gap={1} justifyContent="right">
        <InputButton
          name="cancelDelete"
          type="button"
          variant="text"
          onClick={onClose}
        >
          Cancel
        </InputButton>
        <InputButton
          name="confirmDelete"
          type="button"
          onClick={handleConfirmDeleteClick}
        >
          Delete
        </InputButton>
      </Stack>
    </Stack>
  );
};
